import { useEffect, useCallback, useRef } from "react";
import { IoMdArrowBack } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import VideoCard from "../../components/common/VideoCard";
import { useDispatch, useSelector } from "react-redux";
import { UserlikedVideos, resetLoadingMore } from "../../redux/ApiSlice/LikedVideosSlice";
import SkeletonVideoCard from "../../components/skeleton/SkeletonVideoCard";
import useSelectVideo from "../../hooks/useSelectVideo";

const LikedVideos = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const selectVideo = useSelectVideo();
    const likedScrollRef = useRef(null);
    const { User_likedVideos, loading, loadingMore, nextPage, error } = useSelector((state) => state.userLikedPosts);
    const handleLikedScroll = useCallback(() => {
        const container = likedScrollRef.current;
        if (!container || !nextPage || loadingMore) {
            return;
        }

        const hasReachedBottom =
            container.scrollTop + container.clientHeight >= container.scrollHeight - 60;

        if (hasReachedBottom) {
            dispatch(
                UserlikedVideos({
                    nextUrl: nextPage,
                    isLoadMore: true,
                })
            );
        }
    }, [dispatch, loadingMore, nextPage]);

    useEffect(() => {
        dispatch(resetLoadingMore());
        dispatch(UserlikedVideos());
    }, [dispatch]);

    useEffect(() => {
        const container = likedScrollRef.current;
        if (!container) return;

        container.addEventListener("scroll", handleLikedScroll);

        return () => {
            container.removeEventListener("scroll", handleLikedScroll);
        };
    }, [handleLikedScroll]);

    const handleSelectVideo = async (id) => {
        await selectVideo(id, User_likedVideos, location.pathname, 'liked', nextPage);
    };

    return (
        <>
            <div
                ref={likedScrollRef}
                className="h-full bg-white dark:bg-[#1E1D21] overflow-y-scroll relative"
            >
                <div className="sticky top-0 z-50">
                    <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                        <IoMdArrowBack
                            onClick={() => navigate("/settings/youractivity")}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                        />
                        <p className="font-bold text-[16px] text-black dark:text-black">
                            Liked Videos
                        </p>
                    </div>
                </div>

                {loading ? (
                    <SkeletonVideoCard />
                ) : (
                    <>
                        <div className="grid grid-cols-3 p-2 gap-1">
                            {User_likedVideos?.map((item) => (
                                <VideoCard
                                    key={item.id}
                                    id={item.id}
                                    belowDecs={true}
                                    body={item.post_description}
                                    threeDots={false}
                                    video={item.video_url}
                                    view_count={item.view_count}
                                    likes_count={item.likes_count}
                                    authorId={item.created_by?.id}
                                    selectVideo={handleSelectVideo}
                                    avatar_url={item.created_by.avatar_url}
                                />
                            ))}
                        </div>
                        {loadingMore && (
                            <div className="flex justify-center items-center py-4">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                            </div>
                        )}
                    </>
                )}

                {error && !loading && (
                    <div className="flex justify-center items-center mt-5">
                        <p className="text-[12px] text-red-400 dark:text-red-500">
                            {error}
                        </p>
                    </div>
                )}

                {User_likedVideos?.length === 0 && !loading && !error && (
                    <div className="flex justify-center items-center">
                        <p className="text-[12px] mt-5 text-gray-400 dark:text-gray-500">
                            No liked videos
                        </p>
                    </div>
                )}
            </div>
        </>
    )
}

export default LikedVideos